import React, {FC, useEffect, useState} from 'react'
import {getErrorMessageForNonStandardAndStandardResponse} from '../../util/NetworkErrorUtil';
import {
  getBookingOrderNumber,
  moveAssetToDifferentBooking,
  updateInventoryCorrection
} from '../../components/inventory-correction/requests/inventory-correction-requests';
import InventoryModal from '../../components/inventory-correction/InventoryModal';
import InventoryCorrectionsTable
  from "../../components/inventory-correction/InventoryCorrectionsTable/InventoryCorrectionsTable";
import DeleteInterchangeConfirmationDialog
  from "../../components/inventory-correction/DeleteInterchangeConfirmationDialog";
import ReviewModal from '../../components/inventory-correction/ReviewModal';
import {InventoryCorrectionReasons,} from "../../components/constants/inventory-correction-constants";
import {getComparator, stableSort} from "../../util/Sorting";
import {isBuyerAccount, isSupplierAccount} from "../../util/AccountUtils";
import GroupSortFilter from "../../components/GroupSortFilter";
import {useSnackbar} from "../../context/snackbar-context";
import {SecurSpaceContainer, SecurSpaceReportContainer, SecurSpaceReportTitle} from "../../components/common";
import {SortFilterContainer} from "../../components/common/reports/sortFilter";


const modalTypeOptions = {REVIEW: "REVIEW", ACCEPT: "ACCEPT", REJECT: "REJECT"}

const InventoryCorrections = ({
                                fetchInventoryCorrections,
                                inventoryCorrections = [],
                                rawCorrections = [],
                                sortByOptions = [],
                                account,
                                defaultSearchFilter,
                              }) => {
  const [rejectReason, setRejectReason] = useState('');
  const [errorMessages, setErrorMessages] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [selectInventory, setSelectInventory] = useState(null);
  const [inventoryToReview, setInventoryToReview] = useState(null);
  const [inventoryCorrectionsWithFilter, setInventoryCorrectionsWithFilter] = useState([]);
  const [searchFilter, setSearchFilter] = useState(defaultSearchFilter);
  const [modalType, setModalType] = useState(null);
  const [error, setError] = useState(false);
  const [showDeleteInterchangeConfirmationDialog, setShowDeleteInterchangeConfirmationDialog] = useState(false);

  // ── Message Display ─────────────────────
  const { snackbarShowMessage } = useSnackbar();

  const handleOpen = () => setModalType(modalTypeOptions.REVIEW);

  const handleClose = () => {
    setModalType(null);
    setErrorMessages('');
    setError(false);
    setRejectReason('');
    setOrderNumber('');
  }

  const handleUnknownBookingNumberClick = () => {
    handleClose();
    setShowDeleteInterchangeConfirmationDialog(true);
  };

  const handleCloseDeleteInterchangeConfirmationDialog = () => {
    setSelectInventory(null);
    setShowDeleteInterchangeConfirmationDialog(false);
  };

  const handleDeleteInterchangeSuccess = () => {
    handleCloseDeleteInterchangeConfirmationDialog();
    fetchInventoryCorrections();
  };

  const updateInventory = (inventoryId, data) => {
    updateInventoryCorrection(inventoryId, data)
      .then(() => {
        snackbarShowMessage('Inventory Corrections Updated successfully', 'success');
        handleClose();
        setRejectReason('');
        fetchInventoryCorrections();
      })
      .catch((error) => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error')
      })
  };

  const filterCallback = (filter) => {
    setSearchFilter(filter);
  };

  useEffect(() => {
    let filteredData = searchFilter.searchFilter ? inventoryCorrections.filter((item) =>
      item.buyerName?.toLowerCase().includes(searchFilter?.searchFilter?.toLowerCase()) ||
      item.orderNumber?.toLowerCase().includes(searchFilter?.searchFilter?.toLowerCase())
    ) : inventoryCorrections;

    // Sort by the selected option
    if (searchFilter.sortBy && searchFilter.sortBy !== 'none') {
      filteredData = stableSort(filteredData, getComparator(searchFilter.sortDir, searchFilter.sortBy));
    }
    setInventoryCorrectionsWithFilter(filteredData);
  }, [inventoryCorrections, searchFilter]);

  const rejectInventoryCorrection = () => {
    if (!rejectReason) {
      setErrorMessages('Please enter the reason for rejection');
      setError(true);
      return;
    }
    const inventoryId = selectInventory.item.id;
    const data = {
      status: "REJECTED",
      rejectionReason: rejectReason
    }
    updateInventory(inventoryId, data);
    setError(false);
    setErrorMessages('');

  };

  const acceptInventoryCorrection = () => {
    const inventoryId = selectInventory.item.id;
    const data = {
      status: "APPROVED",
      rejectReason: ""
    }
    if (selectInventory?.item?.reason === InventoryCorrectionReasons.ASSET_DOES_NOT_BELONG_TO_BUYER) {
      handleOpen();
    } else {
      updateInventory(inventoryId, data);
    }
  };

  const reviewInventoryCorrection = (item) => {
    const rawInventoryCorrection = rawCorrections.find(correction => correction.id === item.item.id);
    setInventoryToReview(rawInventoryCorrection)
    setSelectInventory(item)
    handleOpen()
  };

  const handleSelectRejectInventoryCorrection = () => {
    setModalType(modalTypeOptions.REJECT)
  }

  const handleSelectAcceptInventoryCorrectionNotBelongToBuyer = () => {
    setModalType(modalTypeOptions.ACCEPT)
  }

  const validateBookingNumber = () => {
    getBookingOrderNumber(orderNumber)
      .then(() => {
        setErrorMessages('');
        setError(false);
        updateInventoryWithBookingNumber()
      })
      .catch(() => {
        setErrorMessages('The booking entered is invalid.')
        setError(true);
      })
  };

  const updateInventoryWithBookingNumber = () => {
    const inventoryActivityId = selectInventory.item.locationInventoryActivityGetVO.id;
    moveAssetToDifferentBooking(inventoryActivityId, orderNumber)
      .then(() => {
        snackbarShowMessage('Booking moved successfully', 'success');
        handleClose();
        setOrderNumber('');
        fetchInventoryCorrections();
      })
      .catch((error) => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error')
      })
  };

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Inventory Corrections</SecurSpaceReportTitle>
        <SortFilterContainer>
          <GroupSortFilter
            filterCallback={filterCallback}
            sortByOption={sortByOptions}
            sortDir={searchFilter.sortDir}
            searchPlaceholder={`Type to search by booking number or ${isBuyerAccount(account) ? 'supplier' : 'buyer'} name`}
            debounceTime={1000}
          />
        </SortFilterContainer>
        <InventoryCorrectionsTable
          inventoryCorrections={inventoryCorrectionsWithFilter}
          handleReviewItem={reviewInventoryCorrection}
          isSupplier={isSupplierAccount(account)}
        />
        <ReviewModal
          inventoryCorrection={inventoryToReview}
          modalType={modalType}
          handleClose={handleClose}
          showNewBookingNumberForm={handleSelectAcceptInventoryCorrectionNotBelongToBuyer}
          handleAccept={acceptInventoryCorrection}
          handleReject={handleSelectRejectInventoryCorrection}
          openModal={modalType === modalTypeOptions.REVIEW}
          isSupplier={isSupplierAccount(account)}
        />
        <InventoryModal
          correctionReason={selectInventory?.item?.reason}
          onDeleteInterchangeActivity={handleUnknownBookingNumberClick}
          type={modalType}
          error={error}
          handleClose={handleClose}
          rejectReason={rejectReason}
          setRejectReason={setRejectReason}
          orderNumber={orderNumber}
          setOrderNumber={setOrderNumber}
          handleSubmit={modalType === modalTypeOptions.ACCEPT ? validateBookingNumber : rejectInventoryCorrection}
          errorMessages={errorMessages}
          modalTypeOptions={modalTypeOptions}
          handleReject={rejectInventoryCorrection}
        />
        <DeleteInterchangeConfirmationDialog
          inventoryActivity={selectInventory?.item}
          onClose={handleCloseDeleteInterchangeConfirmationDialog}
          onSuccess={handleDeleteInterchangeSuccess}
          open={showDeleteInterchangeConfirmationDialog}
        />
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  )
}

export default InventoryCorrections;
